import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, Move, MousePointerClick } from "lucide-react";



const TangramGame = () => {
    const navigate = useNavigate();
    const svgRef = useRef(null);
    
    // Pieces state
    // Initial pieces setup
    
    const [pieces, setPieces] = useState([
        { id: 0, type: 'ST', x: 50, y: 50, r: 0, color: '#e74c3c' },
        { id: 1, type: 'ST', x: 150, y: 50, r: 0, color: '#e67e22' },
        { id: 2, type: 'MT', x: 100, y: 150, r: 0, color: '#f1c40f' },
        { id: 3, type: 'LT', x: 300, y: 150, r: 0, color: '#2ecc71' },
        { id: 4, type: 'LT', x: 500, y: 150, r: 0, color: '#1abc9c' },
        { id: 5, type: 'SQ', x: 50, y: 250, r: 0, color: '#3498db' },
        { id: 6, type: 'PA', x: 200, y: 250, r: 0, color: '#9b59b6' }, 
    ]);
    

    // Puzzle Definitions
    const PUZZLES = {
        square: {
            name: "The Square",
            desc: "The classic Tangram challenge. Form a perfect square.",
            outline: (
                <rect 
                    x="258" y="158" width="283" height="283" 
                    fill="none" stroke="#94a3b8" strokeWidth="3" strokeDasharray="10 6" 
                />
            ),
            solution: [
                // LT1: Left Triangle (Tip at Center)
                { id: 3, x: 400, y: 300, r: 45 }, 
                // LT2: Top Triangle (Tip at Center)
                { id: 4, x: 400, y: 300, r: 315 }, 
                
                // MT: Bottom-Right Corner (Tip at Corner)
                { id: 2, x: 541, y: 441, r: 135 }, 
                
                // SQ: Sitting in the middle gap (Right of Center)
                // Center (400,300).
                // SQ Tilted. Tip at (400,300).
                { id: 5, x: 471, y: 300, r: 45 }, 
                
                // PA: Filling the Right Edge
                // Between LT2 Top-Right corner and SQ/MT.
                // Tip at (541, 158).
                { id: 6, x: 541, y: 158, r: 135 },

                // ST1: Filling Bottom Gap (between LT1 and MT)
                { id: 0, x: 400, y: 441, r: 0 },

                // ST2: Filling Top-Right gap?
                // Actually PA fills it.
                // What's left? 
                // Gap between SQ and MT?
                { id: 1, x: 471, y: 371, r: 90 }
            ] 
        },
        house: {
            name: "The House",
            desc: "A cozy place. Can you build the roof and walls?",
            // Scaled Up (~1.5x of previous)
            outline: (
                <path 
                    d="M 300 300 L 500 300 L 400 150 Z M 320 300 L 320 500 L 480 500 L 480 300 Z" 
                    fill="none" stroke="#94a3b8" strokeWidth="3" strokeDasharray="10 6" 
                />
            ),
            solution: []
        },
        swan: {
            name: "The Swan",
            desc: "Graceful and elegant.",
            // Scaled Up
            outline: (
                <path 
                    d="M 300 400 L 500 400 L 400 500 Z M 300 400 L 300 200 L 250 250 Z" 
                    transform="scale(1.2) translate(-50, -50)" 
                    fill="none" stroke="#94a3b8" strokeWidth="3" strokeDasharray="10 6" 
                />
            ),
            solution: []
        }
    };

    const [currentPuzzle, setCurrentPuzzle] = useState("square");
    const [draggingId, setDraggingId] = useState(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    


    const handleMouseDown = (e, id) => {
        e.preventDefault();
        const piece = pieces.find(p => p.id === id);
        // Get mouse pos relative to SVG
        const svg = svgRef.current.getBoundingClientRect();
        const mx = e.clientX - svg.left;
        const my = e.clientY - svg.top;
        
        setDraggingId(id);
        setOffset({ x: mx - piece.x, y: my - piece.y });
    };
    
    const handleMouseMove = (e) => {
        if (draggingId === null) return;
        
        const svg = svgRef.current.getBoundingClientRect();
        const mx = e.clientX - svg.left;
        const my = e.clientY - svg.top;
        
        setPieces(pieces.map(p => {
            if (p.id === draggingId) {
                return { ...p, x: mx - offset.x, y: my - offset.y };
            }
            return p;
        }));
    };
    
    const handleMouseUp = () => {
        // Snap to grid (25px for finer control)
        if (draggingId !== null) {
            setPieces(pieces.map(p => {
                if (p.id === draggingId) {
                    const snap = 25;
                    return { 
                        ...p, 
                        x: Math.round(p.x / snap) * snap, 
                        y: Math.round(p.y / snap) * snap 
                    };
                }
                return p;
            }));
        }
        setDraggingId(null);
    };
    
    const handleDoubleClick = (id) => {
        setPieces(pieces.map(p => {
            if (p.id === id) {
                return { ...p, r: (p.r + 45) % 360 };
            }
            return p;
        }));
    };
    
    // Path Definitions

    
    // Better Paths (Standardized Unit 100 = Small Leg)
    // Better Paths (Standardized Unit 100 = Small Leg)
    const PATH_D = {
        ST: "M 0 0 L 100 0 L 0 100 Z",
        MT: "M 0 0 L 141.4 0 L 0 141.4 Z", 
        LT: "M 0 0 L 200 0 L 0 200 Z", 
        SQ: "M 0 0 L 100 0 L 100 100 L 0 100 Z", 
        PA: "M 0 0 L 100 0 L 200 100 L 100 100 Z" // Corrected: Slant 100, Height 100.
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8">
            <div className="w-full max-w-4xl px-4 flex justify-between items-center mb-6">
                 <button 
                    onClick={() => navigate('/games')} 
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                 >
                    <ArrowLeft className="w-5 h-5 mr-1" /> 
                    Back to Games
                 </button>
                 <h1 className="text-3xl font-bold text-gray-800">Tangram Sandbox</h1>
                 <button onClick={() => window.location.reload()} className="text-blue-600"><RotateCcw /></button>
            </div>
            
            <div className="bg-white p-4 rounded-xl shadow-lg border relative overflow-hidden">
                <p className="text-center text-gray-500 mb-2 flex items-center justify-center gap-2">
                    <Move className="w-4 h-4" /> Drag to move 
                    <span className="text-gray-300">|</span> 
                    <MousePointerClick className="w-4 h-4 ml-1" /> Double click to rotate
                </p>
                <div className="absolute top-4 left-4 bg-blue-50 p-3 rounded-lg border border-blue-100 max-w-xs z-10 pointer-events-none select-none">
                    <h3 className="font-bold text-blue-900 text-sm mb-1">Goal: {PUZZLES[currentPuzzle].name}</h3>
                    <p className="text-xs text-blue-800">
                        {PUZZLES[currentPuzzle].desc}
                    </p>
                </div>
                <div className="absolute top-4 right-4 z-10">
                     <button 
                        onClick={() => {
                            if (PUZZLES[currentPuzzle].solution.length > 0) {
                                if (window.confirm("Show solution? This will reset your progress.")) {
                                    // Merge solution state into pieces
                                    const sol = PUZZLES[currentPuzzle].solution;
                                    // Need to be careful: if we have 2 pieces of same type, how do we match?
                                    // SOLUTION array uses specific IDs now. match by ID.
                                    const newPieces = pieces.map(p => {
                                        const match = sol.find(s => s.id === p.id);
                                        return match ? { ...p, ...match } : p;
                                    });
                                    setPieces(newPieces);
                                }
                            } else {
                                alert("Solution coming soon for this shape!");
                            }
                        }}
                        className={`
                            px-3 py-1 rounded text-sm font-bold shadow transition-colors
                            ${PUZZLES[currentPuzzle].solution.length > 0 
                                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer' 
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
                        `}
                     >
                         Show Solution
                     </button>
                </div>
                <svg 
                    ref={svgRef}
                    width="800" 
                    height="600" 
                    className="bg-gray-100 border rounded cursor-crosshair touch-none"
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {/* Grid (Optional) */}
                    <defs>
                        <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="gray" strokeWidth="0.5" opacity="0.2"/>
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />
                    
                    {/* Target Area */}
                    {PUZZLES[currentPuzzle].outline}

                    {pieces.map(p => (
                        <g 
                            key={p.id} 
                            transform={`translate(${p.x}, ${p.y}) rotate(${p.r})`}
                            onMouseDown={(e) => handleMouseDown(e, p.id)}
                            onDoubleClick={() => handleDoubleClick(p.id)}
                            style={{ cursor: 'pointer' }}
                        >
                            <path 
                                d={PATH_D[p.type] || PATH_D['SQ']} 
                                fill={p.color} 
                                stroke="white" 
                                strokeWidth="2"
                                opacity={draggingId === p.id ? 0.8 : 1}
                                className="transition-opacity hover:opacity-90"
                            />
                            {/* Rotation Handle/Center indicator? */}
                            <circle cx="0" cy="0" r="3" fill="rgba(0,0,0,0.2)" pointerEvents="none" />
                        </g>
                    ))}
                </svg>
            </div>
            
            <div className="mt-6 max-w-2xl text-center text-gray-600">
                <p className="mb-4">Select a puzzle:</p>
                <div className="flex justify-center gap-4">
                    {Object.keys(PUZZLES).map(key => (
                         <button
                            key={key}
                            onClick={() => setCurrentPuzzle(key)}
                            className={`
                                w-24 h-24 flex items-center justify-center text-sm font-bold rounded-lg border-2 transition-all
                                ${currentPuzzle === key 
                                    ? 'bg-blue-600 text-white border-blue-800 scale-105 shadow-md' 
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-blue-300'
                                }
                            `}
                         >
                             {PUZZLES[key].name}
                         </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TangramGame;
